/*
 * Выключатель «Автоодобрение» (docs/ai-lab.md). Пилюля, а не второй тумблер: рядом стоит
 * тумблер режима публикаций, и два одинаковых тумблера читались бы одним органом.
 *
 * Включённое — подложка акцента, рамка и точка акцентом, подпись `--accent-ink`: заметно
 * боковым зрением, но это не заливка — залитое пятно акцента в шапке одно, у главной кнопки.
 * Выключенное — серый контур со словом «выкл». Подсказка — геометрия `.tip` `ModeSwitch`.
 *
 * ЛЕСТНИЦА ШАПКИ (docs/ui-design.md §4.1). Орган есть только у бренда, которому конфиг
 * разрешает автоодобрение (сегодня AI Lab), а у такого бренда нет сервиса генерации — полосы
 * хода в его шапке не бывает, и это 232px запаса против ширины, по которой мерили лестницу.
 * Орган с полной подписью ~175px. Ступени — те же числа, что у соседей: ≤1279 подпись
 * сокращается до «Авто», ≤640 остаются точка и контур (слово уходит в `aria-label` и подсказку,
 * из DOM не удаляется). Линейкой в Chromium лестница с этим органом НЕ перемерялась; довод
 * «полосы хода рядом нет» держит растяжка — тест `packages/brand-config/src/resolve-channel.test.ts`
 * краснеет на бренде с автоодобрением и писателем, и тогда шапку перемеряют по §4.1.
 */

.wrap {
  position: relative;
  display: flex;
  align-items: center;
  flex: none;
}

.sw {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  border-radius: var(--r-btn);
  border: 1px solid var(--border);
  background: transparent;
  font-family: var(--sans);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: -0.01em;
  white-space: nowrap;
  color: var(--muted);
  cursor: pointer;
  transition:
    background-color 0.12s var(--ease),
    border-color 0.12s var(--ease);
}
.sw:hover {
  background: var(--bg-alt);
}
/* Индикатор без права переключать: вид тот же, курсор обычный — «смотреть можно». */
.sw:disabled {
  cursor: default;
}
.sw:disabled:hover {
  background: transparent;
}
.sw:focus-visible {
  outline: 2px solid var(--violet);
  outline-offset: 2px;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: none;
  /* Выключено — кольцо: «места под точку» хватает, а самой точки нет. */
  box-shadow: inset 0 0 0 1.5px var(--muted);
}

.state {
  font-weight: 500;
}

.short {
  display: none;
}

/* Включено — акцент подложкой, рамкой и точкой. */
.on {
  background: var(--violet-bg);
  border-color: var(--violet);
  color: var(--accent-ink);
}
.on:hover {
  background: var(--violet-bg);
  border-color: var(--violet-dark);
}
.on:disabled:hover {
  background: var(--violet-bg);
}
.on .dot {
  background: var(--violet);
  box-shadow: none;
}

/* Состояние не загрузилось: серо и без утверждения о положении. */
.unknown {
  cursor: default;
  background: var(--neutral-bg);
}
.unknown:hover {
  background: var(--neutral-bg);
}

@media (max-width: 1279px) {
  .long {
    display: none;
  }
  .short {
    display: inline;
  }
}

@media (max-width: 640px) {
  .short,
  .state {
    display: none;
  }
  .sw {
    padding: 0 10px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .sw {
    transition: none;
  }
}

/* Подсказка — как у `ModeSwitch`: прозрачностью, а не `visibility`, чтобы `aria-describedby`
   оставался в дереве доступности. */
.tip {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 30;
  width: 300px;
  max-width: 78vw;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  border-radius: var(--r-ctl);
  background: var(--white);
  border: 1px solid var(--border);
  box-shadow: var(--sh-float);
  font-size: 12.5px;
  line-height: 1.45;
  color: var(--ink-2);
  text-align: left;
  white-space: normal;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s var(--ease);
}
.tip b {
  font-size: 13px;
  color: var(--ink);
}
.tipNote {
  color: var(--muted);
}

.wrap:hover .tip,
.wrap:focus-within .tip {
  opacity: 1;
}

@media (prefers-reduced-motion: reduce) {
  .tip {
    transition: none;
  }
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
